import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNotify, type Identifier } from "ra-core";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import { useMemberCapability } from "@/components/atomic-crm/providers/commons/useMemberCapability";
import { Switch } from "@/components/ui/switch";

/**
 * Client switch: pre-select "Charge [saved method] now" when sending this
 * client's invoices (fixed services). Staff can still change it per send.
 * Not in the summary views, so it reads and writes the table directly.
 */
export const ChargeOnSendSwitch = ({
  resource,
  id,
}: {
  resource: "companies" | "contacts";
  id: Identifier;
}) => {
  const canEdit = useMemberCapability("proposals.send");
  const notify = useNotify();
  const queryClient = useQueryClient();
  const queryKey = ["charge-on-send", resource, String(id)];

  const { data: enabled = false } = useQuery({
    queryKey,
    queryFn: async () => {
      const { data, error } = await supabase
        .from(resource)
        .select("charge_on_send")
        .eq("id", id)
        .single();
      if (error) throw error;
      return Boolean(data?.charge_on_send);
    },
    staleTime: 30_000,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: async (next: boolean) => {
      const { error } = await supabase
        .from(resource)
        .update({ charge_on_send: next })
        .eq("id", id);
      if (error) throw error;
      return next;
    },
    onSuccess: (next) => {
      queryClient.setQueryData(queryKey, next);
      queryClient.invalidateQueries({
        queryKey: ["client-saved-payment-methods"],
      });
    },
    onError: () => notify("Could not update charge on send", { type: "error" }),
  });

  return (
    <label className="flex w-full items-center justify-between gap-3 px-2 py-1">
      <span className="text-xs text-muted-foreground">
        {enabled
          ? "Invoices are charged to the saved card when sent"
          : "Invoices are emailed with a payment link"}
      </span>
      <Switch
        checked={enabled}
        disabled={!canEdit || isPending}
        onCheckedChange={(next) => mutate(next)}
        aria-label="Charge saved payment method on send"
      />
    </label>
  );
};
